import { useCallback, useEffect, useState } from "react";
import { api, setUnauthorizedHandler } from "./api";
import { Dashboard } from "./components/Dashboard";
import { Login } from "./components/Login";
import type { Theme } from "./components/ThemeToggle";

const THEME_KEY = "arxiv-monitor.theme";

function readTheme(): Theme {
  try {
    return localStorage.getItem(THEME_KEY) === "light" ? "light" : "dark";
  } catch {
    return "dark";
  }
}

export default function App() {
  const [authenticated, setAuthenticated] = useState<boolean | null>(null);
  const [theme, setTheme] = useState<Theme>(readTheme);

  useEffect(() => {
    document.documentElement.setAttribute("data-theme", theme);
    try {
      localStorage.setItem(THEME_KEY, theme);
    } catch {
      /* private mode; the attribute above still applies for this session */
    }
  }, [theme]);

  const toggleTheme = useCallback(() => {
    setTheme((current) => (current === "dark" ? "light" : "dark"));
  }, []);

  useEffect(() => {
    // A 401 from any request — including an expired 24h session mid-use —
    // drops straight back to the login screen.
    setUnauthorizedHandler(() => setAuthenticated(false));
    api
      .session()
      .then((result) => setAuthenticated(result.authenticated))
      .catch(() => setAuthenticated(false));
  }, []);

  if (authenticated === null) {
    return (
      <div className="login-wrap">
        <div className="kicker">Loading…</div>
      </div>
    );
  }

  if (!authenticated) {
    return <Login onSuccess={() => setAuthenticated(true)} theme={theme} onToggleTheme={toggleTheme} />;
  }

  return <Dashboard theme={theme} onToggleTheme={toggleTheme} onSignOut={() => setAuthenticated(false)} />;
}
